import { Box } from '@chakra-ui/layout';
import { useDraggable } from '@dnd-kit/core';
import classNames from 'classnames';
import { type CSSProperties, type PropsWithChildren, useEffect } from 'react';

import type { PositionedItem } from '../PageConfigProvider/types/item';
import styles from './EditableGrid.module.css';
import { useGrid } from './GridProvider';
import { Resizer } from './Resizer';

type DraggableProps = PropsWithChildren<{
  id: string;
  item: PositionedItem;
  style?: CSSProperties;
  focused?: boolean;
  selected?: boolean;
  dragTransform?: { x: number; y: number } | null;
  setDragTransform?: (t: { x: number; y: number } | null) => void;
  selectedIds?: Set<string>;
}>;

export const Draggable = ({
  id,
  item,
  style: styleProp,
  children,
  focused,
  selected,
  dragTransform,
  setDragTransform,
  selectedIds,
}: DraggableProps) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: item,
  });

  const { tempResize, itemDragging } = useGrid();

  const isActive = itemDragging?.id === id;
  const isMultiDrag =
    !!itemDragging &&
    !!selectedIds &&
    selectedIds.has(itemDragging.id) &&
    selectedIds.has(id) &&
    selectedIds.size > 1;

  useEffect(() => {
    if (!setDragTransform) return;
    if (isActive && transform) {
      setDragTransform({ x: transform.x, y: transform.y });
    }
    return () => {
      if (isActive) setDragTransform(null);
    };
  }, [isActive, transform, setDragTransform]);

  const translateFromDrag =
    !isActive && isMultiDrag && dragTransform
      ? `translate3d(${dragTransform.x}px, ${dragTransform.y}px, 0)`
      : null;

  const transformStyle = (() => {
    if (tempResize?.id === item.id) {
      return `translate3d(${tempResize.x}px, ${tempResize.y}px, 0)`;
    }
    if (isActive && transform) {
      return `translate3d(${transform.x}px, ${transform.y}px, 0)`;
    }
    if (translateFromDrag) return translateFromDrag;
    return undefined;
  })();

  const style = {
    ...(transformStyle && { transform: transformStyle }),
    ...(tempResize?.id === item.id && {
      width: `calc(100% + ${tempResize.width}px)`,
    }),
    ...(tempResize?.id === item.id && {
      height: `calc(100% + ${tempResize.height}px)`,
    }),
    ...styleProp,
  };

  return (
    <Box
      backgroundColor="transparent"
      minWidth={0}
      minHeight={0}
      width="100%"
      height="100%"
      borderRadius="var(--chakra-radii-md)"
      ref={setNodeRef}
      userSelect="none"
      style={style}
      className={classNames(styles.draggable, {
        [styles.focused ?? '']: focused,
        [styles.selected ?? '']: selected,
      })}
      {...listeners}
      {...attributes}
    >
      <Resizer id={id} item={item}>
        {children}
      </Resizer>
    </Box>
  );
};
